// ---------------------------------------------------------------------------
// Columns Renderer (PB-E)
// ---------------------------------------------------------------------------
// Zone-bearing primitive — distributes child blocks across 2 or 3 columns.
// Children are stored under `props.columns` as an array of column groups; each
// column group is a `{ children: PuckComponent[] }` zone. Empty columns render
// an HTML comment sentinel rather than collapsing.

import { registerRenderer } from "../registry.js";
import type { PuckComponent, RenderContext } from "../types.js";
import { attr } from "../escape.js";

type ColumnsCount = "2" | "3";
type ColumnsGap = "sm" | "md" | "lg";

interface ColumnsProps {
  columns?: ColumnsCount | string;
  gap?: ColumnsGap | string;
  /** New shape: explicit per-column zones. */
  columnContent?: unknown;
  /** Legacy/simple shape: flat children array, distributed round-robin. */
  children?: unknown;
}

const VALID_COUNT: ReadonlySet<ColumnsCount> = new Set(["2", "3"]);
const VALID_GAP: ReadonlySet<ColumnsGap> = new Set(["sm", "md", "lg"]);

function resolveCount(value: unknown): 2 | 3 {
  if (typeof value === "string" && VALID_COUNT.has(value as ColumnsCount)) {
    return Number(value) as 2 | 3;
  }
  if (typeof value === "number" && (value === 2 || value === 3)) {
    return value;
  }
  return 2;
}

function resolveGap(value: unknown): ColumnsGap {
  return typeof value === "string" && VALID_GAP.has(value as ColumnsGap)
    ? (value as ColumnsGap)
    : "md";
}

function coerceChildren(value: unknown): PuckComponent[] {
  if (!Array.isArray(value)) return [];
  return value.filter(
    (entry): entry is PuckComponent =>
      typeof entry === "object"
      && entry !== null
      && typeof (entry as PuckComponent).type === "string",
  );
}

function distributeColumns(
  props: ColumnsProps,
  count: 2 | 3,
): PuckComponent[][] {
  // Preferred shape: per-column zones.
  if (Array.isArray(props.columnContent)) {
    const columns: PuckComponent[][] = [];
    for (let i = 0; i < count; i += 1) {
      const raw = props.columnContent[i];
      const zoneChildren =
        raw && typeof raw === "object" && "children" in (raw as object)
          ? (raw as { children?: unknown }).children
          : raw;
      columns.push(coerceChildren(zoneChildren));
    }
    return columns;
  }

  // Legacy shape: flat children, distributed round-robin so order is stable.
  const flat = coerceChildren(props.children);
  const columns: PuckComponent[][] = Array.from({ length: count }, () => []);
  flat.forEach((child, index) => {
    const target = columns[index % count];
    if (target) target.push(child);
  });
  return columns;
}

function renderColumn(
  children: PuckComponent[],
  ctx: RenderContext,
  index: number,
): string {
  if (!ctx.renderBlock || children.length === 0) {
    return `  <div class="ease-columns__col" data-column-index="${attr(String(index))}">
    <!-- column ${index} empty -->
  </div>`;
  }
  const inner = children.map((child) => ctx.renderBlock!(child)).join("\n");
  return `  <div class="ease-columns__col" data-column-index="${attr(String(index))}">
${inner}
  </div>`;
}

function render(props: Record<string, unknown>, ctx: RenderContext): string {
  const p = props as ColumnsProps;
  const count = resolveCount(p.columns);
  const gap = resolveGap(p.gap);
  const grouped = distributeColumns(p, count);

  const columnsHtml = grouped
    .map((children, index) => renderColumn(children, ctx, index))
    .join("\n");

  return `<section class="ease-section ease-columns ease-columns--${attr(String(count))} ease-columns--gap-${attr(gap)}">
${columnsHtml}
</section>`;
}

registerRenderer("columns", { render });
